import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, throttleTime } from 'rxjs/operators';
import { RouterStateService } from '../../root/router-state.service';
import { SnackBarService } from '../../shared/snackbar.service';
import { Experience, InternalDriverListItem, PayloadType } from '../../types';
import { isHttpErrorResponse } from '../../utilities/isHttpErrorResponse';
import { EXPERIENCE_ID } from './experience-constants';

export interface UpdateReq {
  description: string;
  minimumYears: number;
  name: string;
  payloadTypesIDs: string[];
  recommendedYears: number;
  userIds: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ExperienceService {
  private users$$ = new BehaviorSubject<InternalDriverListItem[]>([]);
  private usersThrottle$$ = new Subject();
  private usersShared$ = this.users$$.pipe(shareReplay(1));

  public get users$(): Observable<InternalDriverListItem[]> {
    this.usersThrottle$$.next();
    return this.usersShared$;
  }

  private experiences$$ = new BehaviorSubject<Experience[]>([]);
  private experiencesThrottle$$ = new Subject();
  private experiencesShared$ = this.experiences$$.pipe(shareReplay(1));

  public get experiences$(): Observable<Experience[]> {
    this.experiencesThrottle$$.next();
    return this.experiencesShared$;
  }

  private payloadTypes$$ = new BehaviorSubject<PayloadType[]>([]);
  private payloadTypesThrottle$$ = new Subject();
  private payloadTypesShared$ = this.payloadTypes$$.pipe(shareReplay(1));

  public get payloadTypes$(): Observable<PayloadType[]> {
    this.payloadTypesThrottle$$.next();
    return this.payloadTypesShared$;
  }

  private currentExperience$$ = new BehaviorSubject<Experience>(null);
  private currentExperienceShared$ = this.currentExperience$$.asObservable().pipe(shareReplay(1));
  public currentExperience$(): Observable<Experience> {
    this.experiencesThrottle$$.next(); // need to trigger experiences if someone tries to get one
    return this.currentExperienceShared$;
  }

  constructor(
    private httpClient: HttpClient,
    private logger: NGXLogger,
    private snackBar: SnackBarService,
    private routerStateService: RouterStateService,
  ) {
    this.usersThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadUsers();
    });
    this.experiencesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadExperiences();
    });
    this.payloadTypesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadPayloadTypes();
    });
    this.listenForExperience();
  }

  private async loadUsers() {
    try {
      const result = await this.httpClient
        .get<{ users: InternalDriverListItem[] }>(`${environment.api}/v1/internal/users`)
        .toPromise();

      this.users$$.next(result.users);
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to load data');
      }
      this.logger.error(error);
    }
  }

  private async loadExperiences() {
    try {
      const result = await this.httpClient
        .get<{ experiences: Experience[] }>(`${environment.api}/v1/internal/experiences`)
        .toPromise();

      this.experiences$$.next(result.experiences);
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to load data');
      }
      this.logger.error(error);
    }
  }

  private async loadPayloadTypes() {
    try {
      const result = await this.httpClient
        .get<{ payloadTypes: PayloadType[] }>(`${environment.api}/v1/internal/payload_types`)
        .toPromise();

      this.payloadTypes$$.next(result.payloadTypes);
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to load data');
      }
      this.logger.error(error);
    }
  }

  public async updateExperience(id: string, req: any) {
    try {
      await this.httpClient.patch(`${environment.api}/v1/internal/experience/${id}`, req).toPromise();
      this.loadExperiences();
      return true;
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to update experience');
      }
      this.logger.error(error);
    }
  }

  public async createExperience(req: any) {
    try {
      await this.httpClient.put(`${environment.api}/v1/internal/experience`, req).toPromise();
      this.loadExperiences();
      return true;
    } catch (error) {
      if (isHttpErrorResponse(error)) {
        this.snackBar.showError(error?.error?.error?.message);
      } else {
        this.snackBar.showError('Failed to load data');
      }
      this.logger.error(error);
    }
  }

  private async listenForExperience() {
    combineLatest([this.routerStateService.listenForParamChange$(EXPERIENCE_ID), this.experiencesShared$])
      .pipe(map(([expID, expers]) => (!!expID ? expers.find((order) => order.id === expID) : null)))
      .subscribe((value) => {
        this.currentExperience$$.next(value);
      });
  }
}
